/**
 * Elegant botanical/geometric ornaments used as watermark-style decorations
 * on beige/cream sections to add richness without breaking minimal luxury.
 *
 * Usage:
 *   <section className="relative overflow-hidden">
 *     <SectionOrnament variant="corners" />
 *     ... content ...
 *   </section>
 *
 * Variants:
 *   - corners: two mirrored botanical sprigs in top-left & bottom-right
 *   - divider: a slim centered horizontal ornament (small diamond with lines)
 *   - dots: soft scattered gold dots (like champagne bubbles)
 *   - frame: fine gold corner brackets in all four corners
 */

const GOLD = "#C7A46A";

function BotanicalSprig({ className = "", flip = false, size = 220 }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 200 200"
      fill="none"
      stroke={GOLD}
      strokeWidth="0.8"
      strokeLinecap="round"
      aria-hidden="true"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      {/* main curved stem */}
      <path d="M20 180 C 40 140, 60 110, 90 90 C 120 70, 150 50, 180 20" opacity="0.75" />
      {/* branch off stem */}
      <path d="M55 128 C 45 118, 40 105, 35 90" opacity="0.6" />
      <path d="M100 82 C 110 72, 118 60, 122 45" opacity="0.6" />
      {/* leaves - elongated ellipses along the stem */}
      <g fill={GOLD} fillOpacity="0.35" stroke="none">
        <ellipse cx="35" cy="150" rx="14" ry="4" transform="rotate(-30 35 150)" />
        <ellipse cx="65" cy="118" rx="12" ry="3.5" transform="rotate(-40 65 118)" />
        <ellipse cx="90" cy="95" rx="14" ry="4" transform="rotate(-50 90 95)" />
        <ellipse cx="118" cy="72" rx="12" ry="3.5" transform="rotate(-55 118 72)" />
        <ellipse cx="150" cy="48" rx="14" ry="4" transform="rotate(-58 150 48)" />
        <ellipse cx="42" cy="102" rx="9" ry="3" transform="rotate(60 42 102)" />
        <ellipse cx="112" cy="52" rx="9" ry="3" transform="rotate(58 112 52)" />
      </g>
      {/* small buds */}
      <circle cx="178" cy="24" r="3" fill={GOLD} fillOpacity="0.55" />
      <circle cx="34" cy="90" r="2.5" fill={GOLD} fillOpacity="0.5" />
    </svg>
  );
}

function Divider() {
  return (
    <svg
      className="mx-auto"
      width="140"
      height="16"
      viewBox="0 0 140 16"
      fill="none"
      stroke={GOLD}
      strokeWidth="0.9"
      aria-hidden="true"
    >
      <line x1="0" y1="8" x2="55" y2="8" opacity="0.55" />
      <line x1="85" y1="8" x2="140" y2="8" opacity="0.55" />
      <path d="M70 2 L76 8 L70 14 L64 8 Z" fill={GOLD} fillOpacity="0.7" stroke={GOLD} strokeWidth="0.6" />
      <circle cx="70" cy="8" r="1.4" fill="#F8F5F0" />
    </svg>
  );
}

export function OrnamentDivider({ className = "" }) {
  return (
    <div className={`w-full flex justify-center ${className}`} aria-hidden="true">
      <Divider />
    </div>
  );
}

export default function SectionOrnament({ variant = "corners", className = "" }) {
  if (variant === "divider") return <OrnamentDivider className={className} />;

  if (variant === "dots") {
    return (
      <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
        <svg className="absolute inset-0 w-full h-full opacity-[0.35]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="ope-dots" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="4" cy="4" r="1" fill={GOLD} fillOpacity="0.45" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ope-dots)" />
        </svg>
      </div>
    );
  }

  if (variant === "frame") {
    const corner = (pos) => (
      <svg
        key={pos}
        width="52"
        height="52"
        viewBox="0 0 52 52"
        fill="none"
        stroke={GOLD}
        strokeWidth="1"
        className={`absolute ${pos} opacity-60`}
        aria-hidden="true"
      >
        <path d="M2 20 L2 2 L20 2" />
        <line x1="2" y1="2" x2="14" y2="14" strokeWidth="0.5" opacity="0.7" />
      </svg>
    );
    return (
      <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
        <div style={{ transform: "rotate(0deg)" }}>{corner("top-8 left-8")}</div>
        <div style={{ transform: "rotate(90deg)", transformOrigin: "top right" }}>{corner("top-8 right-8")}</div>
        <div style={{ transform: "rotate(-90deg)", transformOrigin: "bottom left" }}>{corner("bottom-8 left-8")}</div>
        <div style={{ transform: "rotate(180deg)", transformOrigin: "center" }}>{corner("bottom-8 right-8")}</div>
      </div>
    );
  }

  // corners (default) - botanical sprigs
  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <BotanicalSprig className="absolute -top-6 -left-6 opacity-40" size={260} />
      <BotanicalSprig className="absolute -bottom-6 -right-6 opacity-40" size={260} flip />
    </div>
  );
}
